/**
 * `composed@1` — a composition profile whose mechanics are a GameSpec from
 * `@djgg/gameplay-kit/compose`: a maintained TEMPLATE plus a bounded DATA
 * patch (tunables, text slots, kind renames, palette). It rides the same
 * accepted-composition authority as the maintained V1 profiles (release,
 * runtime control, cohort, compiler/closure identities); only the admission
 * rule differs: a composed spec is admitted by proof — it validates and the
 * generic solver completes its objective on ≥2 seeds with the template's
 * signature modules stub-checked — instead of an exact variant lookup.
 *
 * Everything here is deterministic and data-only. The accepted spec names the
 * template and the patch; the game, its presentation, media slots and
 * requirements are DERIVED from them, so an authority cannot carry a game its
 * patch does not produce. A game composed whole (the plan's generate level)
 * carries its GENERATED template, private to its creation, and every reader
 * finds it in the game's library ({@link composedSpecLibraryV1}) exactly as
 * it finds a catalog template.
 */
import {
  canonicalDigest, canonicalJson,
} from '@djgg/gameplay-kit';
import {
  DEFAULT_PROOF_OPTIONS, GENERATED_TEMPLATE_PREFIX, gameVerbProofsIn, getTemplateIn, listTemplatesIn, patchTemplateIn, proveAgainstTemplateAsyncIn,
  resourceSecondsV1, runProofTask, runTemplateProofTaskIn,
  specModuleVersionsIn, templateObligationsIn, templateProofScheduleIn, gameTextFor, rulesTextProblems, validateGeneratedTemplateStructureIn, type ComposeLibraryV1, type GameSpecV1,
  type ProofResultV1, type ProofRunV1, type ProofScheduleV1, type ProofTaskV1, type TemplateOverridesV1, type TemplateV1, type VerbProofV1,
} from '@djgg/gameplay-kit/compose';
import type { GameQualityRequirementV2 } from '../qualitySchemas.js';
import type { GameDesign } from '../design/types.js';
import { backgroundLineProblemsV1, backgroundLineRuleV1, presentationSceneryRuleV1, type SceneryRuleV1 } from './sceneryOnly.js';
import { composedCompositionPresentationV1 } from './composedPresentation.js';
import { SUBJECT_SITUATIONS, subjectSituationsNamedV1 } from './subjectOnly.js';
import { composedLibraryV1, validateGeneratedComposeModulesV1, type GeneratedComposeModuleV1 } from './composedModules.js';

export const COMPOSED_PROFILE = 'composed@1' as const;
/** Selection/candidate ids of composed templates: `composed@1/<template id>`. */
export const COMPOSED_TEMPLATE_PREFIX = `${COMPOSED_PROFILE}/`;
/** Proof seeds every composed admission runs (the owner's ≥2-seed rule). */
export const COMPOSED_PROOF_SEEDS: readonly number[] = Object.freeze([1, 2]);

export interface ComposedGameSpecV1 {
  schemaVersion: 1;
  profile: typeof COMPOSED_PROFILE;
  /** Compose template id (`bump-arcade/sewer-1983`). */
  template: string;
  /** The creation's title; it is also the game's title. */
  title: string;
  /** The reviewed data patch against the template (never `/title`). */
  patch: TemplateOverridesV1;
  /** The generated modules the patch's structure uses (composedModules.ts),
   * each exactly as admitted; absent when the game uses none. */
  modules?: GeneratedComposeModuleV1[];
  /** The generated template (`generated/<name>`) a game composed whole is
   * a patch of: present exactly when `template` is a generated id, and that
   * template. */
  generatedTemplate?: TemplateV1;
}

/** Mechanics identity a composed authority pins: the exact template bytes,
 * the derived game, and every module version in its closure (a generated
 * module also by the digest of its source). */
export interface ComposedConfigV1 {
  kit: 'compose@1';
  templateDigest: string;
  specDigest: string;
  modules: Array<{ id: string; version: number; digest?: string }>;
}

/** The library an accepted composed spec's game runs on: the maintained
 * catalog, its generated modules and its generated template. */
export function composedSpecLibraryV1(spec: Pick<ComposedGameSpecV1, 'modules' | 'generatedTemplate'>): ComposeLibraryV1 {
  return composedLibraryV1(spec.modules ?? [], spec.generatedTemplate ?? null);
}

/** The id of the template a creation's game is composed as, when no catalog
 * template plays like its idea: `generated/<the creation id in kebab case>`.
 * Assigned by the platform (never by a model), deterministic per creation. */
export function composedGeneratedTemplateIdV1(creationId: string): string {
  const kebab = creationId.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  if (!kebab) throw new Error('A generated template needs a creation id with letters or digits');
  return GENERATED_TEMPLATE_PREFIX + (/^[a-z]/.test(kebab) ? kebab : `c-${kebab}`).slice(0, 48).replace(/-+$/, '');
}

/** The library a composed game's CARRIED parts make (an accepted spec, an
 * acknowledgement): the maintained catalog, its generated modules and —
 * exactly when its template id is a generated one — its generated template,
 * validated against the catalog plus those modules and named by that id.
 * Throws when the parts do not make one. */
export function composedCarriedLibraryV1(templateId: string, modules: readonly GeneratedComposeModuleV1[], generatedTemplate: unknown):
  { library: ComposeLibraryV1; generatedTemplate: TemplateV1 | null } {
  const generated = templateId.startsWith(GENERATED_TEMPLATE_PREFIX);
  if (generated !== (generatedTemplate !== undefined)) {
    throw new Error(generated ? 'A generated template game carries its generated template' : 'Only a generated template game carries a generated template');
  }
  if (!generated) return { library: composedLibraryV1(modules, null), generatedTemplate: null };
  // This is the base. Its final verbs can depend on structure applied below;
  // synthesis and the build execute them on the actual derived game, never
  // this base alone.
  const checked = validateGeneratedTemplateStructureIn(composedLibraryV1(modules, null), generatedTemplate);
  if (!checked.ok) throw new Error(`Generated template: ${checked.errors.slice(0, 3).join('; ')}`);
  if (checked.template.id !== templateId) throw new Error('The generated template is not the game\'s template');
  return { library: composedLibraryV1(modules, checked.template), generatedTemplate: checked.template };
}

/** The TEMPLATE kind behind one of a composed game's kinds: the patch's
 * renames (`template kind → game kind`) read backwards; a kind it does not
 * rename is its template's own. The one key a kind keeps across a renaming
 * revision of the same template (its art direction, its art). */
export function composedTemplateKindV1(patch: Pick<TemplateOverridesV1, 'rename'>, kind: string): string {
  return Object.entries(patch.rename ?? {}).find(([, to]) => to === kind)?.[0] ?? kind;
}

export function composedSelectionIdV1(templateId: string): string {
  return COMPOSED_TEMPLATE_PREFIX + templateId;
}

/** The compose template id behind a selection id — a template `library`
 * offers (never a retired revision) — or null for any other id. */
export function composedTemplateOfSelectionV1(selectionId: string, library: ComposeLibraryV1): string | null {
  if (!selectionId.startsWith(COMPOSED_TEMPLATE_PREFIX)) return null;
  const id = selectionId.slice(COMPOSED_TEMPLATE_PREFIX.length);
  return listTemplatesIn(library).some(t => t.id === id) ? id : null;
}

function exactRecord(value: unknown, keys: readonly string[], label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || ![Object.prototype, null].includes(Object.getPrototypeOf(value))
    || Object.keys(value).sort().join(',') !== [...keys].sort().join(',')
    || Reflect.ownKeys(value).length !== keys.length
    || Object.values(Object.getOwnPropertyDescriptors(value)).some(d => !('value' in d))) {
    throw new Error(`Invalid ${label}`);
  }
  return value as Record<string, unknown>;
}

const TITLE_UNSAFE = /[\u0000-\u001f<>]/;

/** A composed game's scenery rule: its render kinds, and the surface contact
 * its modules declare (`sceneryOnly.ts`). */
export function composedSceneryRuleV1(game: GameSpecV1, library: ComposeLibraryV1): SceneryRuleV1 {
  return presentationSceneryRuleV1(composedCompositionPresentationV1(game, library));
}

/** The game a template + patch + title produce on `library` (the maintained
 * catalog plus the game's generated modules), or every reason it does not.
 * The title is written through the template's `/title` slot; a patch never
 * sets it (the creation's title is the game's title). */
export function deriveComposedGameV1(templateId: string, patch: unknown, title: string, library: ComposeLibraryV1):
  { ok: true; game: GameSpecV1; patch: TemplateOverridesV1 } | { ok: false; errors: string[] } {
  if (typeof title !== 'string' || title.length < 1 || title.length > 80 || title.trim() !== title || TITLE_UNSAFE.test(title)) {
    return { ok: false, errors: ['title: the game title must be 1..80 plain characters'] };
  }
  const raw = patch && typeof patch === 'object' && !Array.isArray(patch) ? patch as Record<string, unknown> : null;
  const content = raw?.content && typeof raw.content === 'object' && !Array.isArray(raw.content)
    ? raw.content as Record<string, unknown> : undefined;
  if (content && Object.hasOwn(content, '/title')) return { ok: false, errors: ['content["/title"]: the game keeps the creation title'] };
  const checked = patchTemplateIn(library, templateId, raw ? { ...raw, content: { ...(content ?? {}), '/title': title } } : patch);
  if (!checked.ok) return checked;
  // The background line drives the generated background image: it is this
  // game's scenery (never its own objects, and never a playable structure in
  // a game whose bodies stand on drawn surfaces), checked here so synthesis
  // gets it back as a data-only repair and admission refuses a line that
  // would paint the game's objects or routes the game does not have.
  const scenery = composedSceneryRuleV1(checked.spec, library);
  const named = backgroundLineProblemsV1(checked.spec.presentation.backgroundRole, scenery);
  if (named.length) {
    return { ok: false, errors: [`content["/presentation/backgroundRole"]: the background is the scenery behind this game, but it names ${named.join(', ')}; `
      + backgroundLineRuleV1(scenery)] };
  }
  // A kind's role line is the subject of its generated sprite: the object on
  // its own, never its situation in play (the template's own roles are held to
  // the same rule by the catalogue test; only a patch can introduce one).
  const situated = Object.entries(checked.patch.content ?? {})
    .filter(([path]) => /^\/archetypes\/[^/]+\/role$/.test(path))
    .map(([path, line]) => [path, subjectSituationsNamedV1(line)] as const)
    .filter(([, named]) => named.length);
  if (situated.length) {
    return { ok: false, errors: situated.map(([path, named]) => `content["${path}"]: a role line describes its sprite's subject alone, but it says ${named.join('; ')}; `
      + `describe only the object itself (its shape, colours and parts), never ${SUBJECT_SITUATIONS}`) };
  }
  // A game's own rules text describes this game: its numbers, and only the
  // things it has under the names it gives them (synthesis repairs it).
  if (checked.patch.rules) {
    const problems = rulesTextProblems(getTemplateIn(library, templateId), checked.spec, checked.patch.rename ?? {}, checked.patch.rules);
    if (problems.length) return { ok: false, errors: problems.map(p => `rules: ${p}`) };
  }
  // The canonical reviewed patch: no title, no empty maps.
  const normalized: TemplateOverridesV1 = {};
  const reviewedContent = Object.fromEntries(Object.entries(checked.patch.content ?? {}).filter(([path]) => path !== '/title'));
  if (checked.patch.tunables && Object.keys(checked.patch.tunables).length) normalized.tunables = checked.patch.tunables;
  if (Object.keys(reviewedContent).length) normalized.content = reviewedContent;
  if (checked.patch.rename && Object.keys(checked.patch.rename).length) normalized.rename = checked.patch.rename;
  if (checked.patch.palette) normalized.palette = checked.patch.palette;
  if (checked.patch.structure) normalized.structure = checked.patch.structure;
  if (checked.patch.rules) normalized.rules = checked.patch.rules;
  return { ok: true, game: checked.spec, patch: normalized };
}

/** Structural and cheap: every caller, including request handlers and open
 * transactions, may run it repeatedly. It runs no game; the native verb proofs
 * execute at synthesis and in the build's emitted spec and native-loop gate. */
export function validateComposedGameSpecV1(value: unknown): ComposedGameSpecV1 {
  const carries = (key: string) => !!value && typeof value === 'object' && Object.hasOwn(value, key);
  const raw = exactRecord(value, ['schemaVersion', 'profile', 'template', 'title', 'patch', ...(carries('modules') ? ['modules'] : []),
    ...(carries('generatedTemplate') ? ['generatedTemplate'] : [])], 'composed specification');
  if (raw.schemaVersion !== 1 || raw.profile !== COMPOSED_PROFILE || typeof raw.template !== 'string') {
    throw new Error('Unsupported composed specification');
  }
  const modules = carries('modules') ? validateGeneratedComposeModulesV1(raw.modules) : [];
  let carried: ReturnType<typeof composedCarriedLibraryV1>;
  try { carried = composedCarriedLibraryV1(raw.template, modules, raw.generatedTemplate); }
  catch (error) { throw new Error(`Unsupported composed specification: ${(error as Error).message}`); }
  const derived = deriveComposedGameV1(raw.template, raw.patch, raw.title as string, carried.library);
  if (!derived.ok) throw new Error(`Unsupported composed specification: ${derived.errors.slice(0, 3).join('; ')}`);
  if (canonicalJson(derived.patch) !== canonicalJson(raw.patch)) throw new Error('Composed patch is not in canonical form');
  if (carried.generatedTemplate && canonicalJson(carried.generatedTemplate) !== canonicalJson(raw.generatedTemplate)) {
    throw new Error('Composed generated template is not in canonical form');
  }
  // Every carried module is one the game runs (a spec carries no dead code).
  const used = new Set(specModuleVersionsIn(carried.library, derived.game).map(m => m.id));
  const unused = modules.filter(m => !used.has(m.id)).map(m => m.id);
  if (unused.length) throw new Error(`Composed specification carries generated modules its game does not use: ${unused.join(', ')}`);
  return { schemaVersion: 1, profile: COMPOSED_PROFILE, template: raw.template, title: raw.title as string, patch: derived.patch,
    ...(modules.length ? { modules } : {}), ...(carried.generatedTemplate ? { generatedTemplate: carried.generatedTemplate } : {}) };
}

/** The validated game of an accepted composed spec. */
export function composedGameV1(spec: ComposedGameSpecV1): GameSpecV1 {
  const derived = deriveComposedGameV1(spec.template, spec.patch, spec.title, composedSpecLibraryV1(spec));
  if (!derived.ok) throw new Error(`Composed specification no longer derives: ${derived.errors[0]}`);
  return derived.game;
}

/** The native verb proofs of an accepted composed spec's game (its
 * structure's restated ones, else its template's). */
export function composedVerbProofsV1(spec: ComposedGameSpecV1): VerbProofV1[] {
  return gameVerbProofsIn(composedSpecLibraryV1(spec), spec.template, spec.patch.structure);
}

/** The admission proof's options for a reviewed patch: every proof seed,
 * every signature module stub-checked — the template's own (while the game
 * still uses them) and the ones the patch's structure adds — every event its
 * structure requires observed, and the idle defeat. The idle-defeat run is
 * the builder mission gate's idle probe, proved before a build is accepted:
 * bounty-bob-strikes-back-composed-5 tuned the shift clock to 3000 and failed
 * that gate after admission (idle terminal none). */
const admissionProof = (patch: TemplateOverridesV1) =>
  ({ stubCheck: true, idleDefeat: true, seeds: [...COMPOSED_PROOF_SEEDS], ...(patch.structure ? { structure: patch.structure } : {}) });

/** THE admission proof of a composed spec's game (its template, reviewed
 * patch — renames, structure — and generated modules): the obligations as
 * they apply to the game (objective + required events) on
 * {@link COMPOSED_PROOF_SEEDS} with every signature module stub-checked.
 * Deterministic and CPU-bound (tens of seconds): it yields to the event
 * loop as it goes, so a caller that also serves RPC or heartbeats keeps
 * them alive. The builder's production prover runs the same proof's runs in
 * worker threads instead. */
export function proveComposedAdmissionV1(spec: ComposedGameSpecV1): Promise<ProofResultV1> {
  return proveAgainstTemplateAsyncIn(composedSpecLibraryV1(spec), spec.template, composedGameV1(spec), spec.patch.rename ?? {}, admissionProof(spec.patch));
}

/** {@link proveComposedAdmissionV1} as a schedule of independent solver runs
 * for a caller that runs them in parallel (each with
 * {@link runComposedAdmissionTaskV1}); its result equals the sequential proof. */
export function composedAdmissionScheduleV1(spec: ComposedGameSpecV1): ProofScheduleV1 {
  return templateProofScheduleIn(composedSpecLibraryV1(spec), spec.template, composedGameV1(spec), spec.patch.rename ?? {}, admissionProof(spec.patch));
}

/** One run of a {@link composedAdmissionScheduleV1}, in whichever thread executes it. */
export function runComposedAdmissionTaskV1(spec: ComposedGameSpecV1, task: ProofTaskV1): ProofRunV1 {
  return runTemplateProofTaskIn(composedSpecLibraryV1(spec), spec.template, composedGameV1(spec), spec.patch.rename ?? {}, admissionProof(spec.patch), task);
}

/** Provider-free attribution run for a saved prefix. Its two modes keep the
 * exact spec, seed, solver, inputs and required events; the relaxed mode
 * changes only early-stall detection to the proof's existing outer tick
 * bound. A completed relaxed control proves the prefix did not need a later
 * rule to finish, but neither mode can admit a game: the ordinary strict
 * proof above remains mandatory. */
export function runComposedPrefixLivenessControlV1(spec: ComposedGameSpecV1, task: ProofTaskV1,
  noEarlyStall: boolean): ProofRunV1 {
  if (task.stub !== null) throw new Error('A prefix liveness control does not stub a module');
  const library = composedSpecLibraryV1(spec);
  const game = composedGameV1(spec);
  const obligations = templateObligationsIn(library, spec.template, game, spec.patch.rename ?? {}, spec.patch.structure);
  return runProofTask(game, library.modules, {
    seeds: [...COMPOSED_PROOF_SEEDS], requiredEvents: obligations.requiredEvents, stubCheck: [], idleDefeat: false,
    ...(noEarlyStall ? { stallTicks: DEFAULT_PROOF_OPTIONS.maxTicks } : {}),
  }, task);
}

/** The exact bytes of a template of `library` (a generated one included). */
export async function composedTemplateDigestV1(library: ComposeLibraryV1, templateId: string): Promise<string> {
  return canonicalDigest(getTemplateIn(library, templateId));
}

export async function composedSpecDigestV1(game: GameSpecV1): Promise<string> {
  return canonicalDigest(game);
}

/** Every module version a game runs, a generated one also by its source
 * digest — the module identity an authority and a proof receipt pin. */
export function composedModulePinsV1(game: GameSpecV1, modules: readonly GeneratedComposeModuleV1[]): ComposedConfigV1['modules'] {
  const digests = new Map(modules.map(m => [m.id, m.digest]));
  return specModuleVersionsIn(composedLibraryV1(modules, null), game).map(m => digests.has(m.id) ? { ...m, digest: digests.get(m.id)! } : m);
}

export async function composedConfigV1(spec: ComposedGameSpecV1): Promise<ComposedConfigV1> {
  const game = composedGameV1(spec);
  return { kit: 'compose@1', templateDigest: await composedTemplateDigestV1(composedSpecLibraryV1(spec), spec.template),
    specDigest: await composedSpecDigestV1(game), modules: composedModulePinsV1(game, spec.modules ?? []) };
}

/** Exact recomputation: a config that is not this spec's derivation (a stale
 * template, a changed module, another game) is rejected. */
export async function validateComposedConfigV1(spec: ComposedGameSpecV1, input: unknown): Promise<ComposedConfigV1> {
  const expected = await composedConfigV1(spec);
  if (canonicalJson(input) !== canonicalJson(expected)) throw new Error('Unsupported composed mechanics configuration');
  return expected;
}

/** Requirements derived from the proof obligations as they apply to THIS
 * game (a structural adaptation drops the obligations of modules it removed
 * and adds its structure's signature). Named in the template's own kinds, so
 * an unmodified template's requirements are exactly its obligations. */
export function composedRequirementsV1(spec: ComposedGameSpecV1): GameQualityRequirementV2[] {
  const library = composedSpecLibraryV1(spec);
  const template = getTemplateIn(library, spec.template);
  const rename = spec.patch.rename ?? {};
  const obligations = templateObligationsIn(library, spec.template, composedGameV1(spec), rename, spec.patch.structure);
  const renamed = (event: string) => { const [kind, archetype] = event.split(':'); return archetype !== undefined && rename[archetype] ? `${kind}:${rename[archetype]}` : event; };
  const events = [...template.requirements.events.filter(event => obligations.requiredEvents.includes(renamed(event))),
    ...(spec.patch.structure?.events ?? []).filter(event => !template.requirements.events.includes(event))];
  const rules: Array<[string, GameQualityRequirementV2['semantic']['kind'], string[], string[]]> = [
    ['solver-objective', 'progression', [...template.disclosure.coreVerbs], [`objective-on-${COMPOSED_PROOF_SEEDS.length}-seeds`, ...events]],
    ['signature-modules', 'motion', [...template.disclosure.coreVerbs], obligations.signatureModules.map(id => `stub-fails:${id}`)],
    ['loss-restart', 'lifecycle', ['lose', 'restart'], ['lives-loss', 'fresh-state']],
    ['native-controls', 'agency', ['move', 'action'], ['desktop', 'touch']],
  ];
  return rules.map(([id, kind, actions, consequences]) => ({
    id, mandatory: true, provenance: { source: 'approved-design', reference: composedSelectionIdV1(spec.template) },
    semantic: { kind, revision: 1, parameters: {} }, actors: ['player'], targets: [], preconditions: [], actions, consequences,
    forbiddenOutcomes: ['requirement-omitted'], obligations: [
      { stage: 'final', evidenceKind: 'deterministic-simulation', device: 'deterministic' },
      ...(id === 'native-controls' ? [
        { stage: 'final' as const, evidenceKind: 'native-browser' as const, device: 'desktop' as const },
        { stage: 'final' as const, evidenceKind: 'native-browser' as const, device: 'touch' as const },
      ] : []),
    ],
  }));
}

/** A template's disclosed contract as a GameDesign (retrieval candidate and
 * review display), stated for the derived game: its entities are the game's
 * render kinds, and its summary and rules are the game's own text
 * (gameTextFor: the patch's rules, or the template's text with the game's
 * numbers) — the template's when no derived game is given.
 * Throws when that text misdescribes the game. */
export function composedTemplateDesignV1(library: ComposeLibraryV1, templateId: string,
  derived?: { game: GameSpecV1; patch: TemplateOverridesV1 }): GameDesign {
  const template = getTemplateIn(library, templateId);
  const game = derived?.game ?? template.spec;
  const text = gameTextFor(template, game, derived?.patch ?? {});
  if (!text.ok) throw new Error(`The game's rules misdescribe it: ${text.errors.join('; ')}`);
  return {
    summary: text.text.pitch,
    coreVerbs: [...text.text.disclosure.coreVerbs],
    mechanics: text.text.disclosure.mechanics,
    entities: Object.entries(game.archetypes).map(([name, a]) => ({ name, role: a.role })),
    controls: text.text.disclosure.controls,
    winCondition: text.text.disclosure.winCondition,
  };
}

/** Human-readable, deterministic list of what a patch changes — the review
 * step's "what changes" list, rendered the same way everywhere. */
/** The change list is a human summary shown at review; it travels in the
 * synthesis reply and the parser enforces these same bounds. */
export const COMPOSED_CHANGES_MAX = 40;
export const COMPOSED_CHANGE_LINE_MAX_LEN = 300;

/** Fit a change summary to the shared bounds: an over-long line ends in an
 * ellipsis, and past the line limit the remainder is counted, never dropped silently. */
function boundComposedChangesV1(lines: string[]): string[] {
  const fitted = lines.map(line => line.length <= COMPOSED_CHANGE_LINE_MAX_LEN ? line
    : `${line.slice(0, COMPOSED_CHANGE_LINE_MAX_LEN - 1)}…`);
  if (fitted.length <= COMPOSED_CHANGES_MAX) return fitted;
  const kept = fitted.slice(0, COMPOSED_CHANGES_MAX - 1);
  return [...kept, `…and ${fitted.length - kept.length} more changes`];
}

export function describeComposedPatchV1(library: ComposeLibraryV1, templateId: string, patch: TemplateOverridesV1): string[] {
  const template = getTemplateIn(library, templateId);
  const lines: string[] = [];
  for (const [from, to] of Object.entries(patch.rename ?? {})) lines.push(`${from} is renamed ${to}`);
  for (const [path, value] of Object.entries(patch.tunables ?? {})) {
    const parts = path.split('/').filter(Boolean);
    const field = parts[parts.length - 1]!;
    const before = readTunable(template.spec, path);
    lines.push(`${parts[1]} ${field}: ${typeof before === 'number' ? `${before} → ` : ''}${value}`);
  }
  for (const [path, value] of Object.entries(patch.content ?? {})) {
    const parts = path.split('/').filter(Boolean);
    lines.push(parts[0] === 'archetypes' ? `${parts[1]} is "${value}"` : `${parts.join(' ')}: "${value}"`);
  }
  if (patch.palette) lines.push(`palette: ${patch.palette.join(' ')}`);
  return lines.length ? boundComposedChangesV1(lines) : [`Plays exactly as the ${template.title} template`];
}

/** The tunable fields a creator can read as they stand. A field outside this
 * list (ticks, gaps, capacities) has no honest plain name or unit, so it is
 * counted, not named. */
const PLAIN_TUNABLE_FIELDS: Readonly<Record<string, string>> = {
  speed: 'speed', maxSpeed: 'top speed', lives: 'lives', hp: 'health',
};

/** A clock's capacity setting, as the game's own HUD names and reads it: the
 * capacity of a seconds `rule.resource` (a shift clock). Null for any other path. */
function plainClock(game: GameSpecV1, path: string): { label: string; seconds: (ticks: unknown) => string } | null {
  const parts = path.split('/').filter(Boolean);
  if (parts[0] !== 'globals' || parts.length !== 3 || parts[2] !== 'capacity') return null;
  const meter = game.globals.find(g => (g.key ?? g.use) === parts[1]);
  const config = meter?.config as { label?: unknown; display?: unknown } | undefined;
  if (meter?.use !== 'rule.resource' || config?.display !== 'seconds' || typeof config.label !== 'string') return null;
  const seconds = (ticks: unknown) => {
    const s = typeof ticks === 'number' ? resourceSecondsV1(ticks) : NaN;
    return s >= 120 && s % 60 === 0 ? `${s / 60} minutes` : s === 1 ? '1 second' : `${s} seconds`;
  };
  return { label: `${config.label.toLowerCase()} clock`, seconds };
}

const plainName = (name: string): string => name.replaceAll('-', ' ');
const sentence = (line: string): string => line.charAt(0).toUpperCase() + line.slice(1);

/** What a revision changed, from the live game's patch to the revised one, in
 * the creator's words: never a module, field or path name. A setting either
 * patch leaves out reads as the template's own. Deterministic; empty when the
 * two patches describe the same game. */
export function describeComposedRevisionV1(library: ComposeLibraryV1, templateId: string,
  base: TemplateOverridesV1, revised: TemplateOverridesV1): string[] {
  const template = getTemplateIn(library, templateId);
  const keys = (a?: Record<string, unknown>, b?: Record<string, unknown>) => [...new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})])].sort();
  const named = (kind: string) => plainName(revised.rename?.[kind] ?? kind);
  const lines: string[] = [];
  let otherSettings = 0, otherWords = false;
  for (const kind of keys(base.rename, revised.rename)) {
    const was = plainName(base.rename?.[kind] ?? kind), now = named(kind);
    if (was !== now) lines.push(`${was} is now called ${now}`);
  }
  for (const path of keys(base.tunables, revised.tunables)) {
    const was = base.tunables?.[path] ?? readTunable(template.spec, path), now = revised.tunables?.[path] ?? readTunable(template.spec, path);
    if (was === now) continue;
    const parts = path.split('/').filter(Boolean);
    const field = PLAIN_TUNABLE_FIELDS[parts[parts.length - 1]!];
    const clock = plainClock(template.spec, path);
    if (parts[0] === 'archetypes' && field) lines.push(`${named(parts[1]!)} ${field}: ${String(was)} → ${String(now)}`);
    else if (clock) lines.push(`${clock.label}: ${clock.seconds(was)} → ${clock.seconds(now)}`);
    else otherSettings++;
  }
  for (const path of keys(base.content, revised.content)) {
    const now = revised.content?.[path];
    if (base.content?.[path] === now) continue;
    const parts = path.split('/').filter(Boolean);
    if (parts[0] !== 'archetypes') otherWords = true;
    else lines.push(now === undefined ? `${named(parts[1]!)} is back to its original look` : `${named(parts[1]!)} is now "${now}"`);
  }
  if ((base.palette ?? []).join(' ') !== (revised.palette ?? []).join(' ')) lines.push('the game\'s colours changed');
  if (otherWords) lines.push('some of the game\'s wording changed');
  if (otherSettings) lines.push(otherSettings === 1 ? 'one timing or balance setting changed' : `${otherSettings} timing or balance settings changed`);
  return lines.length ? boundComposedChangesV1(lines.map(sentence)) : [];
}

/** Whether a revised patch plays as exactly the same game as the live one. It
 * compares the derived games, so a patch that only restates a template value,
 * or lists its entries in another order, is the same game. */
export function composedRevisionSameGameV1(library: ComposeLibraryV1, templateId: string, title: string,
  base: TemplateOverridesV1, revised: TemplateOverridesV1): boolean {
  const before = deriveComposedGameV1(templateId, base, title, library);
  const after = deriveComposedGameV1(templateId, revised, title, library);
  return before.ok && after.ok && canonicalJson(before.game) === canonicalJson(after.game);
}

function readTunable(game: GameSpecV1, path: string): unknown {
  const parts = path.split('/').filter(Boolean);
  let node: unknown;
  let rest: string[];
  if (parts[0] === 'archetypes') {
    node = game.archetypes[parts[1]!]?.modules.find(m => m.use === parts[3])?.config;
    rest = parts.slice(4);
  } else {
    node = game.globals.find(g => (g.key ?? g.use) === parts[1])?.config;
    rest = parts.slice(2);
  }
  for (const key of rest) node = node && typeof node === 'object' ? (node as Record<string, unknown>)[key] : undefined;
  return node;
}
